import type { BotConfig, ExchangeAccount, FreePeriodPackage, User } from "@investhinks/api-client";
import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { Badge, Button } from "reactstrap";

import { subscriptionsEndpoints } from "../../api";
import getCurrencySymbol from "../../utils/getCurrencySymbol";
import isVipOrZeroPriceTier from "../../utils/isVipOrZeroPriceTier";
import { getStrategyName } from "../../utils/risks";
import useBots from "../bots/hooks/useBots";
import { LegalFooter } from "../legal";
import GeneralPerformanceGraph from "../stats/GeneralPerformanceGraph";
import { formatAmount, formatSignedAmount } from "../utils/currency";
import useExchangeAccount from "./hooks/useExchangeAccount";
import cardHistory from "./cardHistory";

interface GrantFreeAccount {
  id: number | string;
  name?: string;
  [key: string]: unknown;
}

interface ExchangeAccountCardProps {
  id: number | string | null;
  isMine?: boolean;
  onClick?: () => void;
  isAdmin?: boolean;
  onGrantFree?: (account: GrantFreeAccount) => void;
  bot?: BotConfig | null;
  isLoadingBots?: boolean;
  exchangeAccount?: ExchangeAccount | null;
  isLoadingExchangeAccount?: boolean;
  isLoadingHistory?: boolean;
  historySince?: number;
}

interface SessionState {
  user?: User;
}

const ExchangeAccountCard: React.FC<ExchangeAccountCardProps> = ({
  id,
  isMine,
  onClick,
  isAdmin,
  onGrantFree,
  bot: botProp,
  isLoadingBots: isLoadingBotsProp,
  exchangeAccount: exchangeAccountProp,
  isLoadingExchangeAccount: isLoadingExchangeAccountProp,
  isLoadingHistory,
  historySince,
}) => {
  const sessionUser = useSelector(({ sessionState }: { sessionState?: SessionState }) => sessionState?.user);
  const { t, i18n } = useTranslation("exchangeAccounts", { keyPrefix: "exchangeAccountCard" });
  const { t: tPeriods } = useTranslation("users", { keyPrefix: "freePeriods" });

  const [packages, setPackages] = useState<FreePeriodPackage[]>([]);

  // Check if parent is managing exchange account data
  const parentManagesExchangeAccount = isLoadingExchangeAccountProp !== undefined;

  // Only fetch exchange account locally if parent is NOT managing it
  const { exchangeAccount: fetchedExchangeAccount, isLoading: isLoadingExchangeAccountFetched } = useExchangeAccount(
    parentManagesExchangeAccount ? undefined : (id ?? undefined)
  );

  // Use prop if parent manages exchange account, otherwise use fetched data
  const exchangeAccount = parentManagesExchangeAccount ? exchangeAccountProp : fetchedExchangeAccount;
  const isLoadingExchangeAccount = parentManagesExchangeAccount
    ? isLoadingExchangeAccountProp
    : isLoadingExchangeAccountFetched;

  // Check if parent is managing bot data (isLoadingBotsProp is explicitly passed)
  const parentManagesBots = isLoadingBotsProp !== undefined;

  // Only fetch bots locally if parent is NOT managing bot data
  const { bots: [fetchedBot] = [], isLoading: isLoadingBotsFetched } = useBots({
    filters: id ? { exchange_account: { id: { $eq: id } } } : undefined,
    sort: { createdAt: "down" },
    pagination: { pageSize: 99 },
    depsToReset: [id],
    mine: isMine,
    cacheKey: id ? `bots_ea_${id}` : undefined,
    autoLoad: !parentManagesBots, // Only auto-load if parent is NOT managing bot data
  });

  // Use prop if parent manages bots, otherwise use fetched data
  const bot = parentManagesBots ? botProp : fetchedBot;
  const isLoadingBots = parentManagesBots ? isLoadingBotsProp : isLoadingBotsFetched;

  // Type-safe property access
  const status = exchangeAccount?.status;
  const name = exchangeAccount?.name;
  const exchange = exchangeAccount?.exchange;
  const hidden = exchangeAccount?.hidden;
  const userConfig = bot?.userConfig;
  const strategy = bot?.strategy;
  const stats = bot?.stats;
  const disabled = bot?.disabled;
  const subscription = bot?.subscription;
  // statistics is not in BotConfig schema but may be populated by the API
  const botStatistics = bot && typeof bot === "object" && "statistics" in bot ? bot.statistics : undefined;
  const statistics =
    botStatistics && typeof botStatistics === "object" && "history" in botStatistics
      ? { history: Array.isArray(botStatistics.history) ? botStatistics.history : undefined }
      : undefined;
  const botUser = bot?.user;
  const user = botUser ?? sessionUser;
  const history = statistics?.history;

  // Fetch free period packages for admin view
  useEffect(() => {
    if (!isAdmin || !id) return;

    const fetchPackages = async () => {
      try {
        const numericId = typeof id === "string" ? parseInt(id, 10) : id;
        if (!numericId) return;
        const { data } = await subscriptionsEndpoints.getPackagesByCredentials(numericId);
        setPackages(data || []);
      } catch (err) {
        console.error("Failed to load packages:", err);
        setPackages([]);
      }
    };

    fetchPackages();
  }, [isAdmin, id]);

  // Get active packages (not expired and active)
  const activePackages = useMemo(() => {
    return packages.filter((pkg) => {
      return pkg.isActive && moment(pkg.endDate).isAfter(moment());
    });
  }, [packages]);

  // Calculate total free days remaining (merge overlapping ranges)
  const totalFreeDays = useMemo(() => {
    if (activePackages.length === 0) return 0;

    const now = moment();

    // Get all date ranges, clipping start to now if in the past
    const ranges = activePackages
      .map((pkg) => {
        const start = moment(pkg.startDate);
        const end = moment(pkg.endDate);
        return {
          start: start.isAfter(now) ? start : now.clone(),
          end: end,
        };
      })
      .filter((r) => r.end.isAfter(r.start));

    if (ranges.length === 0) return 0;

    // Sort by start date
    ranges.sort((a, b) => a.start.valueOf() - b.start.valueOf());

    // Merge overlapping ranges
    const merged = [{ ...ranges[0] }];
    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1];
      const current = ranges[i];
      if (current.start.isSameOrBefore(last.end)) {
        // Overlapping - extend end if needed
        if (current.end.isAfter(last.end)) {
          last.end = current.end;
        }
      } else {
        // Non-overlapping - add new range
        merged.push({ ...current });
      }
    }

    // Sum days from merged ranges
    return merged.reduce((sum, r) => sum + Math.max(0, r.end.diff(r.start, "days")), 0);
  }, [activePackages]);

  const filteredHistory = useMemo(() => {
    return cardHistory(history, historySince ?? moment().subtract(1, "month").valueOf(), moment().valueOf());
  }, [history, historySince]);

  const paymentError =
    !isVipOrZeroPriceTier(user) && !!subscription && subscription?.pm_status && ["failed", "unpaid", "failed_escalated"].includes(subscription.pm_status);
  const credentialsError = status === "INVALID";
  const error = paymentError || credentialsError;

  const symbol = useMemo(() => getCurrencySymbol(stats?.baseCurrency ?? ""), [stats?.baseCurrency]);

  // Determine if performance is positive
  const isPositive = (stats?.profit_percentage ?? 0) > 0;
  const isNegative = (stats?.profit_percentage ?? 0) < 0;

  if (isLoadingExchangeAccount || isLoadingBots) {
    return (
      <div className="bot-card animate-fade-in">
        <div className="bot-card-content">
          <Skeleton height={160} style={{ borderRadius: "10px" }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bot-card mb-3 animate-fade-in ${error ? "bot-card-error" : ""} ${disabled ? "bot-card-paused" : ""}`}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      {/* Chart area wrapper - contains chart background and content */}
      <div style={{ position: "relative" }}>
        {/* Background Chart */}
        <div className="bot-card-chart">
          <GeneralPerformanceGraph
            isMinimalist
            isLoading={isLoadingBots || isLoadingHistory}
            data={filteredHistory}
            style={{ height: "100%" }}
          />
        </div>

        {/* Content */}
        <div className="bot-card-content">
          {/* Header */}
          <div className="bot-card-header">
            <div>
              <h4 className="bot-name">
                {hidden && <i className="fas fa-eye-slash mr-2" style={{ opacity: 0.5 }} />}
                {name}
              </h4>
              <div className="bot-meta">
                {getStrategyName(strategy, typeof userConfig?.risk === "string" ? userConfig.risk : undefined)}
                <span style={{ opacity: 0.3, margin: "0 6px" }}>|</span>
                {exchange?.name}
                {stats?.username && (
                  <>
                    <span style={{ opacity: 0.3, margin: "0 6px" }}>|</span>
                    {stats.username}
                  </>
                )}
              </div>
            </div>

            <div className="bot-status">
              {error && (
                <span className="status-badge status-error">
                  <span className="status-dot" />
                  {t("actionRequiredBadge")}
                </span>
              )}
              {disabled && (
                <span className="status-badge status-paused">
                  <span className="status-dot" />
                  {t("pausedBadge")}
                </span>
              )}
              {!bot && (
                <span className="status-badge status-disconnected">
                  <span className="status-dot" />
                  {t("notConnectedBotBadge")}
                </span>
              )}
              {bot && !error && !disabled && (
                <span className="status-badge status-active">
                  <span className="status-dot" />
                  {t("activeBadge")}
                </span>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="bot-card-stats">
            <div className="stat">
              <div className="label">{t("balanceLabel")}</div>
              <div className="value">
                {formatAmount(stats?.balance, i18n.language)} {symbol}
              </div>
            </div>

            <div className="stat">
              <div className="label">{isNegative ? t("lossesLabel") : t("profitLabel")}</div>
              <div className={`value ${isPositive ? "positive" : ""} ${isNegative ? "negative" : ""}`}>
                {formatSignedAmount(stats?.absolute_profit, i18n.language)} {symbol}
              </div>
              {stats?.profit_percentage != null && !isNaN(stats?.profit_percentage) && (
                <div
                  className={`value ${isPositive ? "positive" : ""} ${isNegative ? "negative" : ""}`}
                  style={{ fontSize: "0.75rem", opacity: 0.7 }}
                >
                  {formatSignedAmount(stats.profit_percentage, i18n.language)}%
                </div>
              )}
            </div>

            <div className="stat">
              <div className="label">{t("drawdown")}</div>
              <div className={`value ${(stats?.drawdown ?? 0) > 10 ? "negative" : ""}`}>
                {formatAmount(stats?.drawdown, i18n.language)}%
              </div>
            </div>
          </div>

          {/* Legal disclaimer for broker data */}
          <LegalFooter type="broker" />

          {/* Action hint */}
          {onClick && (
            <div
              style={{
                marginTop: "16px",
                paddingTop: "12px",
                borderTop: "1px solid rgba(226, 232, 240, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                color: "#94a3b8",
                fontSize: "0.75rem",
              }}
            >
              {isMine ? (
                <>
                  <i className="fas fa-cog mr-1" /> {t("manageAction")}
                </>
              ) : (
                <>
                  <i className="fas fa-search mr-1" /> {t("viewDetailsAction")}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin Section - Outside chart area */}
      {isAdmin && (
        <div style={subscriptionStyles.section}>
          <div style={subscriptionStyles.header}>
            <div style={subscriptionStyles.title}>
              <i className="fas fa-calendar-alt" style={{ marginRight: 6, opacity: 0.7 }} />
              {tPeriods("title")}
            </div>
            {totalFreeDays > 0 && (
              <Badge color="success" style={subscriptionStyles.grantBadge}>
                {totalFreeDays} {tPeriods("freeDays")}
              </Badge>
            )}
          </div>

          {subscription && totalFreeDays === 0 && (
            <div style={subscriptionStyles.details}>
              <div style={subscriptionStyles.row}>
                <span style={subscriptionStyles.label}>{t("nextBilling")}</span>
                <span style={subscriptionStyles.value}>
                  {moment(subscription.pm_currentPeriodEnd).format("DD MMM YYYY")}
                </span>
              </div>
              <div style={subscriptionStyles.row}>
                <span style={subscriptionStyles.label}>{t("daysLabel")}</span>
                <span style={subscriptionStyles.value}>
                  {Math.max(0, moment(subscription.pm_currentPeriodEnd).diff(moment(), "days"))} {tPeriods("days")}
                </span>
              </div>
            </div>
          )}

          {/* Active packages list */}
          {activePackages.length > 0 && (
            <div style={subscriptionStyles.grantsList}>
              {activePackages.slice(0, 2).map((pkg) => {
                const startMoment = moment(pkg.startDate);
                const endMoment = moment(pkg.endDate);
                const daysRemaining = Math.max(0, endMoment.diff(moment(), "days"));
                return (
                  <div key={pkg.id} style={subscriptionStyles.grantItem}>
                    <span style={subscriptionStyles.grantDate}>
                      {startMoment.format("DD MMM")} - {endMoment.format("DD MMM")}
                    </span>
                    <span style={subscriptionStyles.grantDays}>{daysRemaining}d</span>
                    {pkg.reason && (
                      <span style={subscriptionStyles.grantReason}>
                        {pkg.reason.length > 15 ? pkg.reason.substring(0, 15) + "..." : pkg.reason}
                      </span>
                    )}
                  </div>
                );
              })}
              {activePackages.length > 2 && (
                <div style={subscriptionStyles.grantItem}>
                  <span style={subscriptionStyles.grantReason}>
                    {t("andMore", { count: activePackages.length - 2 })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Grant button */}
          {onGrantFree && id && (
            <Button
              color="link"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onGrantFree({ ...(exchangeAccount ?? {}), id, name: name ?? undefined });
              }}
              style={subscriptionStyles.grantButton}
            >
              <i className="fas fa-calendar-plus" style={{ marginRight: 4 }} />
              {tPeriods("grantFree")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const subscriptionStyles: Record<string, React.CSSProperties> = {
  section: {
    padding: "14px 20px 16px",
    backgroundColor: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
    borderRadius: "0 0 12px 12px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  title: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  grantBadge: {
    fontSize: "0.65rem",
    padding: "2px 6px",
  },
  details: {
    display: "flex",
    gap: 16,
    marginBottom: 8,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  label: {
    fontSize: "0.7rem",
    color: "#94a3b8",
  },
  value: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: "#334155",
  },
  grantsList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "8px 10px",
    backgroundColor: "rgba(16, 185, 129, 0.08)",
    borderRadius: 6,
    borderLeft: "2px solid #10b981",
    marginBottom: 8,
  },
  grantItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.7rem",
  },
  grantDate: {
    color: "#64748b",
    minWidth: 50,
  },
  grantDays: {
    fontWeight: 600,
    color: "#10b981",
  },
  grantReason: {
    color: "#94a3b8",
    fontStyle: "italic",
  },
  grantButton: {
    padding: "4px 0",
    fontSize: "0.7rem",
    color: "#3b82f6",
  },
};

export default ExchangeAccountCard;
